/** Activity routes (IA §2 Operate › Activity, §3 trip page). Every view's state is in the URL. */
import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';
import { isIsoDay } from '@/lib/timeRange';

const day = (v: unknown) => (isIsoDay(v) ? v : undefined);

export interface ActivitySearch {
  day?: string;
  groups?: string;
  vehicles?: string;
  driver?: string;
  lens?: 'trips';
  sel?: string;
}

export const activityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity',
  staticData: { permission: 'trips.trip.read' },
  validateSearch: (s: Record<string, unknown>): ActivitySearch => ({
    day: day(s.day),
    groups: str(s.groups),
    vehicles: str(s.vehicles),
    driver: str(s.driver),
    lens: s.lens === 'trips' ? 'trips' : undefined,
    sel: str(s.sel),
  }),
  component: lazyRouteComponent(() => import('./pages/ActivityPage')),
});

export interface TripsSearch {
  q?: string;
  groups?: string;
  vehicle?: string;
  driver?: string;
  min_km?: number;
  event?: string;
  sort?: string;
  selected?: string;
  compare?: string;
}

export const tripsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity/trips',
  staticData: { permission: 'trips.trip.read' },
  validateSearch: (s: Record<string, unknown>): TripsSearch => {
    const km = Number(s.min_km);
    return {
      q: str(s.q),
      groups: str(s.groups),
      vehicle: str(s.vehicle),
      driver: str(s.driver),
      min_km: Number.isFinite(km) && km > 0 ? km : undefined,
      event: str(s.event),
      sort: str(s.sort),
      selected: str(s.selected),
      compare: str(s.compare),
    };
  },
  component: lazyRouteComponent(() => import('./pages/TripsPage')),
});

export const compareRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity/trips/compare',
  staticData: { permission: 'trips.trip.read' },
  validateSearch: (s: Record<string, unknown>): { ids?: string } => ({ ids: str(s.ids) }),
  component: lazyRouteComponent(() => import('./pages/ComparePage')),
});

export const tripRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/trips/$tripId',
  staticData: { permission: 'trips.trip.read' },
  validateSearch: (s: Record<string, unknown>): { tab?: string; event?: string } => ({ tab: str(s.tab), event: str(s.event) }),
  component: lazyRouteComponent(() => import('./pages/TripPage')),
});

export interface EventsSearch {
  groups?: string;
  vehicle?: string;
  driver?: string;
  type?: string;
  severity?: string;
  review?: string;
  video?: 'yes' | 'no';
  sort?: string;
  selected?: string;
  view?: 'map';
}

export const eventsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/activity/events',
  staticData: { permission: 'trips.trip.read' },
  validateSearch: (s: Record<string, unknown>): EventsSearch => ({
    groups: str(s.groups),
    vehicle: str(s.vehicle),
    driver: str(s.driver),
    type: str(s.type),
    severity: str(s.severity),
    review: str(s.review),
    video: s.video === 'yes' || s.video === 'no' ? s.video : undefined,
    sort: str(s.sort),
    selected: str(s.selected),
    view: s.view === 'map' ? 'map' : undefined,
  }),
  component: lazyRouteComponent(() => import('./pages/EventsPage')),
});

/** Old and server-generated paths (tool links, insurance evidence) land on the new views. */
export const tripsAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/trips',
  beforeLoad: () => {
    throw redirect({ to: '/activity/trips' });
  },
});

export const eventsAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/events',
  beforeLoad: () => {
    throw redirect({ to: '/activity/events' });
  },
});

export const eventRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/events/$detectionId',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/activity/events', search: { selected: params.detectionId } });
  },
});
